import React, { FC, MutableRefObject, ReactNode } from 'react';
import { useRouter } from 'next/router';
// import Link from 'next/link';
// import { useIntl } from 'react-intl';

import {
  Box,
  Flex,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  SimpleGrid,
} from '@chakra-ui/react';
import {
  FiChevronDown,
  FiHome,
  FiUser,
  FiBriefcase,
  FiBook,
  FiSettings,
  FiSearch,
  FiLogIn,
} from 'react-icons/fi';

import Logo from '../Logo/Logo';
import BaseImage from '../Base/BaseImage/BaseImage';

import {
  MenuItemsPersonal,
  MenuItemsBusiness,
  MenuItemsAbout,
  MenuItemsServices,
  MenuItemsResources,
} from '../../constants/data/header';

interface HeaderProps {
  refprop?: MutableRefObject<any>;
  mobileNavOnOpen?: () => void;
  goto?: (path: string, waitTime?: number) => void;
}

interface MyMenuItemProps {
  value: string;
}

interface MenuItemProps {
  title: string;
  link: string;
}

interface MenuButtonProps {
  heading: string;
  menuItems: Array<MenuItemProps>;
  menuWidth?: string;
  MyIcon: ReactNode;
}

const Header: FC<HeaderProps> = props => {
  // const { formatMessage } = useIntl();
  // const f: (id: string) => string = (id: string) => formatMessage({ id });
  const router = useRouter();

  const { refprop, mobileNavOnOpen } = props;

  const MyMenuItem: FC<MyMenuItemProps> = ({ value }) => (
    <MenuItem
      fontWeight='500'
      borderBottom='1px'
      borderColor='imColor.primary-light'
      _hover={{ backgroundColor: 'white', opacity: 0.8 }}
    >
      {value}
    </MenuItem>
  );

  const MyMenuButton: FC<MenuButtonProps> = ({
    heading,
    menuItems,
    menuWidth = 'full',
    MyIcon,
  }) => (
    <Menu autoSelect={false} isLazy={true} placement='bottom'>
      <MenuButton
        fontSize={{ lg: 'sm', xl: 'md' }}
        as={Button}
        leftIcon={MyIcon}
        rightIcon={<FiChevronDown color='black' style={{ marginLeft: -6 }} />}
        variant='flushed'
        p='2'
        _focus={{ outline: 'none' }}
        _hover={{ color: 'imColor.primary' }}
      >
        {heading}
      </MenuButton>
      <MenuList w={menuWidth} p='5' color='imColor.black'>
        <SimpleGrid minChildWidth='150px' spacing='10px'>
          {menuItems.map(({ title, link }) => (
            <Box
              as={'a'}
              href={link}
              key={title}
              color={router.pathname === link ? 'imColor.primary' : 'initial'}
              _hover={{ color: 'imColor.gray' }}
              style={{
                textDecorationLine: 'none',
              }}
            >
              <MyMenuItem value={title} />
            </Box>
          ))}
        </SimpleGrid>
      </MenuList>
    </Menu>
  );

  return (
    <Box
      minH={{ base: '64px', md: '76px' }}
      borderTop='4px'
      borderColor='imColor.primary'
      borderBottom='1px'
      borderBottomColor='imColor.primary-light'
    >
      <Flex
        ref={refprop}
        as='nav'
        align='center'
        wrap='nowrap'
        padding={{ base: '0.6rem', md: '0.8rem' }}
        color='black'
      >
        <Box
          display={'flex'}
          alignItems='center'
          flexGrow={1}
          justifyContent='space-between'
        >
          <Flex align='center' w={{ base: '220px', md: '250px' }}>
            <Box as={'a'} href={'/'}>
              <Logo />
            </Box>
          </Flex>
          <Button
            onClick={mobileNavOnOpen}
            variant='default'
            display={{ base: 'flex', lg: 'none' }}
            _focus={{ outline: 'none' }}
          >
            <svg
              fill='black'
              width='18px'
              viewBox='0 0 20 20'
              xmlns='http://www.w3.org/2000/svg'
            >
              <title>Menu</title>
              <path d='M0 3h20v2H0V3zm0 6h20v2H0V9zm0 6h20v2H0v-2z' />
            </svg>
          </Button>
        </Box>
        <Flex display={{ base: 'none', lg: 'initial' }}>
          <Button
            fontSize={{ lg: 'sm', xl: 'md' }}
            variant='flushed'
            p='2'
            leftIcon={<FiHome color='black' style={{ marginRight: -5 }} />}
            _focus={{ outline: 'none' }}
            _hover={{ color: 'imColor.primary' }}
            as={'a'}
            href={'/'}
            style={{ textDecorationLine: 'none' }}
          >
            Home
          </Button>
          <MyMenuButton
            heading={'Personal'}
            menuItems={MenuItemsPersonal}
            menuWidth={'4xl'}
            MyIcon={<FiUser color='black' style={{ marginRight: -5 }} />}
          />
          <MyMenuButton
            heading={'Business'}
            menuItems={MenuItemsBusiness}
            menuWidth={'4xl'}
            MyIcon={<FiBriefcase color='black' style={{ marginRight: -5 }} />}
          />
          <MyMenuButton
            heading={'About'}
            menuItems={MenuItemsAbout}
            menuWidth={'4xl'}
            MyIcon={<FiBook color='black' style={{ marginRight: -5 }} />}
          />
          <MyMenuButton
            heading={'Services'}
            menuItems={MenuItemsServices}
            MyIcon={<FiSettings color='black' style={{ marginRight: -5 }} />}
          />
          <MyMenuButton
            heading={'Resources'}
            menuItems={MenuItemsResources}
            MyIcon={<FiSearch color='black' style={{ marginRight: -5 }} />}
          />
          <Button
            fontSize={{ lg: 'sm', xl: 'md' }}
            variant='flushed'
            p='2'
            leftIcon={<FiLogIn color='black' style={{ marginRight: -5 }} />}
            _focus={{ outline: 'none' }}
            _hover={{ color: 'imColor.primary' }}
            as={'a'}
            href={'/im-priority-new'}
            style={{ textDecorationLine: 'none' }}
          >
            Login
          </Button>
        </Flex>
        <Box
          display={{ base: 'none', lg: 'flex' }}
          alignItems='center'
          flexGrow={1}
        >
          <Flex width='full' align={'center'} justify={'flex-end'}>
            <Box
              as={'a'}
              href='tel:800253733'
              _active={{
                transform: 'scale(0.97)',
              }}
            >
              <BaseImage
                src={'/assets/img/global/call-alfred.png'}
                alt='Call Alfred'
                dimensions={[140, 45]}
                priority={true}
              />
            </Box>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
};

export default Header;
